import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { ENV } from '../../config/env';
import { ChevronDown, Users, CheckCircle, XCircle, Clock, ExternalLink, FileText, Linkedin, Mail, Phone, Search } from 'lucide-react';

interface Application {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    linkedinUrl?: string;
    resumeUrl?: string;
    resumeS3Key?: string;
    status: 'otp_verified' | 'approved' | 'rejected';
    otpVerifiedAt?: string;
    reviewedAt?: string;
    createdAt: string;
}

interface ApiResponse {
    payload: {
        data: Application[];
        pagination: {
            page: number;
            size: number;
            totalPages: number;
            total: number;
        };
    };
    status: {
        msg: string;
        error: boolean;
    };
}

type TabType = 'pending' | 'all' | 'approved' | 'rejected';
type SortType = 'newest' | 'oldest';

/**
 * Review Candidates Page
 * Admin page to review and approve/reject candidate applications
 * Route: /admin/review-candidates
 */
const ReviewCandidates: React.FC = () => {
    const [applications, setApplications] = useState<Application[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<TabType>('pending');
    const [sortBy, setSortBy] = useState<SortType>('newest');
    const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [resumeUrl, setResumeUrl] = useState<string | null>(null);
    const [resumeLoading, setResumeLoading] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');

    // Search state
    const [searchQuery, setSearchQuery] = useState('');

    // Pagination state
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [totalPages, setTotalPages] = useState(0);
    const [total, setTotal] = useState(0);

    // Reset to first page when changing tabs
    useEffect(() => {
        setPage(1);
    }, [activeTab]);

    // Fetch applications when page or activeTab changes
    useEffect(() => {
        fetchApplications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, activeTab]);

    // Fetch resume when modal opens
    useEffect(() => {
        if (showDetailModal && selectedApplication?.id) {
            fetchResumePresignedUrl(selectedApplication.id);
        } else {
            setResumeUrl(null);
        }
    }, [showDetailModal, selectedApplication]);

    const fetchApplications = async () => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('No authentication token found');
            }

            // Build pagination parameters
            const paginationParams = `page=${page}&size=${pageSize}`;

            // Using /application/v1/ endpoints for admin review
            let endpoint: string;
            if (activeTab === 'pending') {
                endpoint = `${ENV.CANDIDATE_SERVICE_URL}/application/v1/pending?${paginationParams}`;
            } else if (activeTab === 'all') {
                endpoint = `${ENV.CANDIDATE_SERVICE_URL}/application/v1/list?${paginationParams}`;
            } else {
                endpoint = `${ENV.CANDIDATE_SERVICE_URL}/application/v1/list?status=${activeTab}&${paginationParams}`;
            }

            const response = await fetch(endpoint, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data: ApiResponse = await response.json();

            if (data.status.error) {
                throw new Error(data.status.msg || 'Failed to fetch applications');
            }

            setApplications(data.payload.data || []);
            setTotal(data.payload.pagination?.total || 0);
            setTotalPages(data.payload.pagination?.totalPages || 0);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            console.error('Error fetching applications:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchResumePresignedUrl = async (applicationId: string) => {
        setResumeLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                console.error('No authentication token found');
                setResumeUrl(null);
                setResumeLoading(false);
                return;
            }

            // Using /application/v1/ endpoint for resume
            const response = await fetch(`${ENV.CANDIDATE_SERVICE_URL}/application/v1/${applicationId}/resume`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = await response.json();

            if (data.status.error) {
                console.error('Error fetching presigned URL:', data.status.msg);
                setResumeUrl(null);
            } else {
                // API returns presignedUrl field
                setResumeUrl(data.payload.presignedUrl || data.payload.url);
            }
        } catch (err) {
            console.error('Error loading resume:', err);
            setResumeUrl(null);
        } finally {
            setResumeLoading(false);
        }
    };

    const handleViewDetails = (application: Application) => {
        setSelectedApplication(application);
        setShowDetailModal(true);
    };

    const handleApprove = async (applicationId: string) => {
        setActionLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                alert('No authentication token found. Please login again.');
                setActionLoading(false);
                return;
            }

            // Using /application/v1/ endpoint for approve
            const response = await fetch(`${ENV.CANDIDATE_SERVICE_URL}/application/v1/${applicationId}/approve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.status.error) {
                throw new Error(data.status.msg || 'Failed to approve application');
            }

            // Refresh list
            await fetchApplications();
            setShowDetailModal(false);
            setSelectedApplication(null);
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to approve application');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectClick = () => {
        setShowRejectModal(true);
    };

    const handleRejectConfirm = async () => {
        if (!rejectionReason.trim()) {
            alert('Please provide a rejection reason');
            return;
        }

        if (!selectedApplication) return;

        setActionLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                alert('No authentication token found. Please login again.');
                setActionLoading(false);
                return;
            }

            // Using /application/v1/ endpoint for reject with reason in body
            const response = await fetch(`${ENV.CANDIDATE_SERVICE_URL}/application/v1/${selectedApplication.id}/reject`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    reason: rejectionReason.trim(),
                }),
            });

            const data = await response.json();

            if (data.status.error) {
                throw new Error(data.status.msg || 'Failed to reject application');
            }

            // Refresh list and close modals
            await fetchApplications();
            setShowRejectModal(false);
            setShowDetailModal(false);
            setSelectedApplication(null);
            setRejectionReason('');
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to reject application');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectCancel = () => {
        setShowRejectModal(false);
        setRejectionReason('');
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'otp_verified':
                return (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded border text-xs font-medium bg-yellow-50 text-yellow-800 border-yellow-200">
                        <Clock size={12} />
                        Pending Review
                    </span>
                );
            case 'approved':
                return (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded border text-xs font-medium bg-green-50 text-green-800 border-green-200">
                        <CheckCircle size={12} />
                        Approved
                    </span>
                );
            case 'rejected':
                return (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded border text-xs font-medium bg-red-50 text-red-800 border-red-200">
                        <XCircle size={12} />
                        Rejected
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded border text-xs font-medium bg-gray-50 text-gray-800 border-gray-200">
                        {status}
                    </span>
                );
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const formatDateTime = (dateString?: string) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getInitials = (firstName?: string, lastName?: string) => {
        if (firstName && lastName) {
            return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
        }
        return '??';
    };

    const filteredApplications = searchQuery.trim()
        ? applications.filter((app) => {
            const q = searchQuery.toLowerCase();
            const name = `${app.firstName || ''} ${app.lastName || ''}`.toLowerCase();
            return name.includes(q) || app.email.toLowerCase().includes(q);
        })
        : applications;

    const sortedApplications = [...filteredApplications].sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortBy === 'newest' ? dateB - dateA : dateA - dateB;
    });


    return (
        <AdminLayout
            showHeader={true}
            headerTitle="Review Candidates"
            headerSubtitle="Review and approve candidate applications"
        >
            {/* White Container with Tabs and Content */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#EDEDED] min-h-[calc(100vh-180px)]">
                {/* Tabs */}
                <div className="flex items-center gap-8 border-b border-[#EDEDED] mb-6">
                    <button
                        onClick={() => setActiveTab('pending')}
                        className={`pb-4 text-sm font-semibold relative ${
                            activeTab === 'pending' ? 'text-[#015FA6]' : 'text-[#757575] hover:text-[#0B111E]'
                        }`}
                    >
                        Pending Review
                        {activeTab === 'pending' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#015FA6] rounded-t-full" />}
                    </button>
                    <button
                        onClick={() => setActiveTab('all')}
                        className={`pb-4 text-sm font-semibold relative ${
                            activeTab === 'all' ? 'text-[#015FA6]' : 'text-[#757575] hover:text-[#0B111E]'
                        }`}
                    >
                        All Applications
                        {activeTab === 'all' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#015FA6] rounded-t-full" />}
                    </button>
                    <button
                        onClick={() => setActiveTab('approved')}
                        className={`pb-4 text-sm font-semibold relative ${
                            activeTab === 'approved' ? 'text-[#015FA6]' : 'text-[#757575] hover:text-[#0B111E]'
                        }`}
                    >
                        Approved
                        {activeTab === 'approved' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#015FA6] rounded-t-full" />}
                    </button>
                    <button
                        onClick={() => setActiveTab('rejected')}
                        className={`pb-4 text-sm font-semibold relative ${
                            activeTab === 'rejected' ? 'text-[#015FA6]' : 'text-[#757575] hover:text-[#0B111E]'
                        }`}
                    >
                        Rejected
                        {activeTab === 'rejected' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#015FA6] rounded-t-full" />}
                    </button>
                </div>

                {/* Action Bar */}
                <div className="flex justify-between items-center mb-6 gap-3">
                    {/* Search Input */}
                    <div className="relative flex-1 max-w-sm">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by name or email..."
                            className="w-full pl-9 pr-3 py-1.5 border border-[#EDEDED] rounded-lg text-xs text-[#515151] placeholder-[#9CA3AF] focus:outline-none focus:border-[#015FA6] focus:ring-1 focus:ring-[#015FA6]/20"
                        />
                    </div>

                    {/* Sort Dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                            className="flex items-center gap-2 px-4 py-1.5 border border-[#EDEDED] rounded-lg text-xs font-medium text-[#515151] hover:bg-gray-50"
                        >
                            {sortBy === 'newest' ? 'Newest First' : 'Oldest First'} <ChevronDown size={14} />
                        </button>

                        {sortDropdownOpen && (
                            <div className="absolute left-0 mt-2 w-36 bg-white border border-[#EDEDED] rounded-lg shadow-lg z-10">
                                <button
                                    onClick={() => {
                                        setSortBy('newest');
                                        setSortDropdownOpen(false);
                                    }}
                                    className={`block w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortBy === 'newest' ? 'text-[#015FA6] font-medium' : ''}`}
                                >
                                    Newest First
                                </button>
                                <button
                                    onClick={() => {
                                        setSortBy('oldest');
                                        setSortDropdownOpen(false);
                                    }}
                                    className={`block w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortBy === 'oldest' ? 'text-[#015FA6] font-medium' : ''}`}
                                >
                                    Oldest First
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Results count */}
                    <span className="text-xs text-[#757575]">
                        {total > 0 ? (
                            <>
                                Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, total)} of {total} application{total !== 1 ? 's' : ''}
                            </>
                        ) : (
                            '0 applications'
                        )}
                    </span>
                </div>

                {/* Applications Table */}
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#015FA6]"></div>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <p className="text-[#FF5210] text-base mb-4">{error}</p>
                        <button
                            onClick={fetchApplications}
                            className="px-6 py-2 bg-[#015FA6] text-white rounded-lg hover:bg-[#014a85]"
                        >
                            Retry
                        </button>
                    </div>
                ) : sortedApplications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center min-h-[400px]">
                        <Users className="w-24 h-24 mb-6 text-[#CBCBCB]" strokeWidth={1} />
                        <p className="text-[#A5A5A5] text-sm font-medium">
                            {activeTab === 'pending'
                                ? 'No applications pending review'
                                : activeTab === 'approved'
                                ? 'No approved applications yet'
                                : activeTab === 'rejected'
                                ? 'No rejected applications'
                                : 'No applications found'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-[#EDEDED]">
                                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B111E]">Name</th>
                                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B111E]">Email</th>
                                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B111E]">Phone</th>
                                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B111E]">Applied Date</th>
                                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B111E]">Status</th>
                                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#0B111E]">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedApplications.map((app) => (
                                    <tr key={app.id} className="border-b border-[#F9F9F9] hover:bg-[#F9F9F9]">
                                        <td className="py-3 px-4 text-sm text-[#0B111E]">
                                            {app.firstName && app.lastName
                                                ? `${app.firstName} ${app.lastName}`
                                                : 'N/A'
                                            }
                                        </td>
                                        <td className="py-3 px-4 text-sm text-[#515151]">{app.email}</td>
                                        <td className="py-3 px-4 text-sm text-[#515151]">{app.phone || 'N/A'}</td>
                                        <td className="py-3 px-4 text-sm text-[#515151]">{formatDate(app.createdAt)}</td>
                                        <td className="py-3 px-4">{getStatusBadge(app.status)}</td>
                                        <td className="py-3 px-4">
                                            <button
                                                onClick={() => handleViewDetails(app)}
                                                className="text-sm font-medium text-[#015FA6] hover:underline"
                                            >
                                                View Details
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between mt-6 pt-6 border-t border-[#EDEDED]">
                                <button
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1}
                                    className="px-4 py-2 text-sm font-medium text-[#015FA6] border border-[#EDEDED] rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                                >
                                    Previous
                                </button>

                                <div className="flex items-center gap-2">
                                    {/* First page */}
                                    {page > 3 && (
                                        <>
                                            <button
                                                onClick={() => setPage(1)}
                                                className="px-3 py-1.5 text-sm font-medium text-[#515151] hover:text-[#015FA6] hover:bg-gray-50 rounded"
                                            >
                                                1
                                            </button>
                                            {page > 4 && <span className="text-[#757575]">...</span>}
                                        </>
                                    )}

                                    {/* Page numbers around current page */}
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter(pageNum => {
                                            return pageNum === page ||
                                                   pageNum === page - 1 ||
                                                   pageNum === page + 1 ||
                                                   pageNum === page - 2 ||
                                                   pageNum === page + 2;
                                        })
                                        .map(pageNum => (
                                            <button
                                                key={pageNum}
                                                onClick={() => setPage(pageNum)}
                                                className={`px-3 py-1.5 text-sm font-medium rounded ${
                                                    pageNum === page
                                                        ? 'bg-[#015FA6] text-white'
                                                        : 'text-[#515151] hover:text-[#015FA6] hover:bg-gray-50'
                                                }`}
                                            >
                                                {pageNum}
                                            </button>
                                        ))}

                                    {/* Last page */}
                                    {page < totalPages - 2 && (
                                        <>
                                            {page < totalPages - 3 && <span className="text-[#757575]">...</span>}
                                            <button
                                                onClick={() => setPage(totalPages)}
                                                className="px-3 py-1.5 text-sm font-medium text-[#515151] hover:text-[#015FA6] hover:bg-gray-50 rounded"
                                            >
                                                {totalPages}
                                            </button>
                                        </>
                                    )}
                                </div>

                                <button
                                    onClick={() => setPage(page + 1)}
                                    disabled={page === totalPages}
                                    className="px-4 py-2 text-sm font-medium text-[#015FA6] border border-[#EDEDED] rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Detail Modal with Resume Preview */}
            {showDetailModal && selectedApplication && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
                    onClick={() => setShowDetailModal(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Left Side - Application Details */}
                        <div className="w-2/5 p-8 overflow-y-auto border-r border-[#EDEDED]">
                            <div className="flex justify-between items-start mb-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl bg-gradient-to-br from-[#015FA6] to-[#0077cc]">
                                        {getInitials(selectedApplication.firstName, selectedApplication.lastName)}
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-semibold text-[#0B111E]">
                                            {selectedApplication.firstName && selectedApplication.lastName
                                                ? `${selectedApplication.firstName} ${selectedApplication.lastName}`
                                                : 'Name Not Provided'}
                                        </h2>
                                        <p className="text-sm text-[#757575] mt-1">
                                            Applied {formatDateTime(selectedApplication.createdAt)}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowDetailModal(false)}
                                    className="text-[#757575] hover:text-[#0B111E] transition-colors p-1"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="mb-6">
                                {getStatusBadge(selectedApplication.status)}
                            </div>

                            <div className="space-y-4">
                                <div className="p-4 bg-[#F9F9F9] rounded-xl">
                                    <label className="text-xs font-medium text-[#757575] uppercase tracking-wider block mb-2">Contact Information</label>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <Mail size={16} className="text-[#757575]" />
                                            <span className="text-sm text-[#0B111E]">{selectedApplication.email}</span>
                                        </div>
                                        {selectedApplication.phone && (
                                            <div className="flex items-center gap-3">
                                                <Phone size={16} className="text-[#757575]" />
                                                <span className="text-sm text-[#0B111E]">{selectedApplication.phone}</span>
                                            </div>
                                        )}
                                        {selectedApplication.linkedinUrl && (
                                            <div className="flex items-center gap-3">
                                                <Linkedin size={16} className="text-[#0077B5]" />
                                                <a
                                                    href={selectedApplication.linkedinUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm text-[#015FA6] hover:underline break-all"
                                                >
                                                    View LinkedIn Profile
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="p-4 bg-[#F9F9F9] rounded-xl">
                                    <label className="text-xs font-medium text-[#757575] uppercase tracking-wider block mb-2">Documents</label>
                                    <div className="space-y-2">
                                        {selectedApplication.resumeS3Key ? (
                                            <div className="flex items-center gap-3">
                                                <FileText size={16} className="text-[#015FA6]" />
                                                <span className="text-sm text-[#0B111E]">Resume uploaded</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-3">
                                                <FileText size={16} className="text-[#CBCBCB]" />
                                                <span className="text-sm text-[#757575]">No resume uploaded</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            {selectedApplication.status === 'otp_verified' && (
                                <div className="flex gap-3 mt-8 pt-6 border-t border-[#EDEDED]">
                                    <button
                                        onClick={() => handleApprove(selectedApplication.id)}
                                        disabled={actionLoading}
                                        className="flex-1 py-3 bg-[#4CAF50] text-white rounded-xl font-medium hover:bg-[#45a049] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                                    >
                                        <CheckCircle size={18} />
                                        {actionLoading ? 'Processing...' : 'Approve'}
                                    </button>
                                    <button
                                        onClick={handleRejectClick}
                                        disabled={actionLoading}
                                        className="flex-1 py-3 bg-[#FF5210] text-white rounded-xl font-medium hover:bg-[#e04a0e] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                                    >
                                        <XCircle size={18} />
                                        {actionLoading ? 'Processing...' : 'Reject'}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Right Side - Resume Preview or LinkedIn */}
                        <div className="w-3/5 bg-[#F9F9F9] p-6 overflow-y-auto">
                            <div className="h-full">
                                {selectedApplication.resumeS3Key ? (
                                    <>
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="text-lg font-semibold text-[#0B111E]">Resume Preview</h3>
                                            {resumeUrl && (
                                                <a
                                                    href={resumeUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm text-[#015FA6] hover:underline flex items-center gap-1"
                                                >
                                                    <ExternalLink size={14} />
                                                    Open in new tab
                                                </a>
                                            )}
                                        </div>

                                        {resumeLoading ? (
                                            <div className="flex items-center justify-center h-full">
                                                <div className="text-center">
                                                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#015FA6]"></div>
                                                    <p className="text-[#757575] mt-4">Loading resume...</p>
                                                </div>
                                            </div>
                                        ) : resumeUrl ? (
                                            <div className="bg-white rounded-xl shadow-sm overflow-hidden" style={{ height: 'calc(100% - 48px)' }}>
                                                <iframe
                                                    src={resumeUrl}
                                                    className="w-full h-full"
                                                    title="Resume Preview"
                                                />
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-center h-full bg-white rounded-xl">
                                                <div className="text-center text-[#757575]">
                                                    <FileText className="w-16 h-16 mx-auto mb-4 text-[#CBCBCB]" />
                                                    <p className="text-sm">Unable to load resume</p>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : selectedApplication.linkedinUrl ? (
                                    <div className="flex items-center justify-center h-full bg-white rounded-xl">
                                        <div className="text-center p-8">
                                            <Linkedin className="w-20 h-20 mx-auto mb-6 text-[#0077B5]" />
                                            <h3 className="text-xl font-semibold text-[#0B111E] mb-2">No Resume Uploaded</h3>
                                            <p className="text-sm text-[#757575] mb-6">This candidate provided their LinkedIn profile instead</p>
                                            <a
                                                href={selectedApplication.linkedinUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-6 py-3 bg-[#0077B5] text-white rounded-xl font-medium hover:bg-[#006399] transition-colors"
                                            >
                                                <Linkedin size={20} />
                                                View LinkedIn Profile
                                            </a>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-full bg-white rounded-xl">
                                        <div className="text-center text-[#757575]">
                                            <FileText className="w-16 h-16 mx-auto mb-4 text-[#CBCBCB]" />
                                            <p className="text-sm">No resume or LinkedIn profile available</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Rejection Reason Modal */}
            {showRejectModal && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center p-4"
                    style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
                    onClick={handleRejectCancel}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold text-[#0B111E]">Reject Application</h3>
                            <button
                                onClick={handleRejectCancel}
                                className="text-[#757575] hover:text-[#0B111E] transition-colors p-1"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <p className="text-sm text-[#757575] mb-4">
                            Please provide a reason for rejecting this application. This will be stored for record keeping.
                        </p>

                        <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Enter rejection reason..."
                            className="w-full px-4 py-3 border border-[#EDEDED] rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#015FA6] focus:border-transparent"
                            rows={4}
                            autoFocus
                        />

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={handleRejectCancel}
                                disabled={actionLoading}
                                className="flex-1 py-3 bg-gray-100 text-[#0B111E] rounded-xl font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRejectConfirm}
                                disabled={actionLoading || !rejectionReason.trim()}
                                className="flex-1 py-3 bg-[#FF5210] text-white rounded-xl font-medium hover:bg-[#e04a0e] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default ReviewCandidates;
